// src/components/Dashboard/TechnicianWorkingStockDetailTable.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Order from '../Order/Order';
import { LoadingSpinner, LoadingContainer, Heading ,DetailContainer, CloseButton, DetailTable, DetailHeader, DetailRow, DetailData, ModalOverlay, ModalContent } from '../../styled/common';

const TechnicianWorkingStockDetailTable = ({ user, material, tech, onClose }) => {
  const [detailData, setDetailData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false); // Step 1: Track if modal is open
  const [selectedOrderNo, setSelectedOrderNo] = useState(null); // Step 1: Track selected order number

  useEffect(() => {
    const fetchDetailData = async () => {
      setIsLoading(true);
      setError(null);
  
      try {
        const response = await axios.get(`/api/inventory?material=${material}&tech_id=${tech}`);
        const inventoryData = response.data;
  
        if (!inventoryData || inventoryData.length === 0) {
          setDetailData([]);
          return;
        }
  
        const { totalWorkingStockOnHand, actions = {} } = inventoryData[0];
  
        if (totalWorkingStockOnHand > 0) {
          const filteredKeys = Object.keys(actions).filter((key) => actions[key][6] > 0);
  
          if (filteredKeys.length === 0) {
            setDetailData([]);
            return;
          }
  
          const detailResponse = await axios.get('/api/salesorders', {
            params: { ids: filteredKeys.join(',') },
          });
          const salesOrders = detailResponse.data;
  
          const mergedData = mergeSalesOrdersWithActions(
            salesOrders,
            actions,
            totalWorkingStockOnHand,
            filteredKeys
          );
  
          setDetailData(mergedData);
        } else {
          setDetailData([]);
        }
      } catch (error) {
        console.error('Error fetching detail data:', error);
        setError('Failed to fetch data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
  
    const mergeSalesOrdersWithActions = (salesOrders, actions, remainingQuantity, filteredKeys) => {
      const mergedData = [];
  
      for (const key of filteredKeys) {
        if (remainingQuantity <= 0) break;
  
        const quantityToUpdate = Math.min(actions[key][6], remainingQuantity);
        remainingQuantity -= quantityToUpdate;
  
        const salesOrder = salesOrders.find((order) => order._id === key);
        if (salesOrder) {
          mergedData.push({
            ...salesOrder,
            workingStockQuantity: quantityToUpdate,
          });
        }
      }
  
      return mergedData;
    };
  
    fetchDetailData();
  }, [material, tech]);  

  const handleOrderClick = (orderNo) => {
    setSelectedOrderNo(orderNo); // Set the selected order number
    setIsModalOpen(true); // Open the modal
  };

  const handleCloseDetail = () => {
    setIsModalOpen(false); // Close the modal
    setSelectedOrderNo(null); // Clear the selected order number
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
      </LoadingContainer>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <DetailContainer>
      <CloseButton onClick={onClose}>Close</CloseButton>
      <Heading>Working Stock Analysis</Heading>
      <DetailTable>
        <thead>
          <tr>
            <DetailHeader>Material</DetailHeader>
            <DetailHeader>Material Description</DetailHeader>
            <DetailHeader>Associated Order</DetailHeader>
            <DetailHeader>Associated NMO</DetailHeader>
            <DetailHeader>Working Stock Quantity</DetailHeader>
            <DetailHeader>Last Updated</DetailHeader>
          </tr>
        </thead>
        <tbody>
          {detailData.map((item) => (
            <DetailRow key={item._id}>
              <DetailData data-label="Material">{item.Material}</DetailData>
              <DetailData data-label="Material Description">{item['Material Description']}</DetailData>
              <DetailData
                data-label="Associated Order"
                onClick={() => handleOrderClick(item['PO Number'].split('-')[1])}
                isClickable={true}
              >
                {item['PO Number'].split('-')[1]}
              </DetailData>
              <DetailData data-label="Associated NMO">{item['Sales Order']}</DetailData>
              <DetailData data-label="Working Stock Quantity">{item.workingStockQuantity}</DetailData>
              <DetailData data-label="Last Updated">{new Date(item.updatedAt).toLocaleString()}</DetailData>
            </DetailRow>
          ))}
        </tbody>
      </DetailTable>

      {isModalOpen && (
        <ModalOverlay>
          <ModalContent>
            <CloseButton onClick={handleCloseDetail}>Close</CloseButton> {/* Step 4: Add close button */}
            {/* Pass selectedOrderNo to the Order component */}
            <Order propOrderNo={selectedOrderNo} user={user}/>
          </ModalContent>
        </ModalOverlay>
      )}
    </DetailContainer>
  );
};

export default TechnicianWorkingStockDetailTable;
